// Per-product persistence for schema-driven steps.
//
// A journey config describes *what* to ask; this describes *where the answers
// go*. Keeping them apart means the generic <JourneyStep/> can drive any
// vertical without importing a product API, and a new vertical needs one entry
// here rather than a new page component.

import { motorApi, petApi, travelApi } from "../api";
import * as lifeApi from "../api/lifeApi";

/**
 * @typedef {Object} DataAdapter
 * @property {(caseId: string) => Promise<Record<string, *>>} load
 *   Existing answers for the case. Must resolve (not reject) on a missing
 *   record — a fresh case has nothing saved yet and that is not an error.
 * @property {(caseId: string, values: Record<string, *>) => Promise<*>} save
 */

const safe = (fn) => (caseId) => fn(caseId).catch(() => ({}));

/** @type {Record<string, DataAdapter>} */
export const DATA_ADAPTERS = {
  travel: {
    load: safe((caseId) => travelApi.fetchTravelData(caseId)),
    save: (caseId, values) =>
      travelApi.syncTravelData(caseId, { insurance_type: "TRAVEL INSURANCE", ...values }),
  },
  pet: {
    load: safe((caseId) => petApi.fetchPetData(caseId)),
    save: (caseId, values) =>
      petApi.syncPetData(caseId, { insurance_type: "PET INSURANCE", ...values }),
  },
  motor: {
    load: safe((caseId) => motorApi.fetchMotorData(caseId)),
    save: (caseId, values) =>
      motorApi.syncMotorData(caseId, { insurance_type: "MOTOR INSURANCE", ...values }),
  },
  life: {
    load: safe((caseId) => lifeApi.fetchLifeData(caseId)),
    save: (caseId, values) => lifeApi.syncLifeData(caseId, values),
  },
};

/** @param {string} journeyKey */
export const getDataAdapter = (journeyKey) => DATA_ADAPTERS[journeyKey] || null;
